import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Briefcase, MessageCircle, Filter, ArrowLeft, Building2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const VendorDiscovery = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', category: '', state: '', district: '', business_type: '' });
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({ name: '', email: '', mobile: '', message: '' });

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const WHATSAPP_NUMBER = '918369211947';

  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (e) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const { data } = await axios.get(`${API}/suppliers/search?${params}`);
      setSuppliers(data);
    } catch (e) {
      console.error('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inquiries`, { ...inquiryForm, source: 'vendor_discovery', supplier_id: selectedSupplier?.id });
      toast.success('Inquiry sent successfully!');
      setSelectedSupplier(null);
      setInquiryForm({ name: '', email: '', mobile: '', message: '' });
    } catch (err) {
      toast.error('Failed to send inquiry');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="glass border-b border-border/40 sticky top-0 z-50">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button onClick={() => navigate('/')} className="text-muted-foreground hover:text-foreground" data-testid="vendor-back-btn">
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div className="flex items-center space-x-2">
                <Sparkles className="h-6 w-6 text-gold" />
                <h1 className="text-2xl font-bold text-navy">Vendor Discovery</h1>
              </div>
            </div>
            <button onClick={() => navigate('/register?role=supplier')} className="px-4 py-2 rounded-md bg-orange text-white font-semibold text-sm" data-testid="register-supplier-cta">
              List Your Business
            </button>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-8 p-6 rounded-lg bg-navy text-white">
          <h2 className="text-3xl font-bold mb-2 text-gold">Find Verified Suppliers</h2>
          <p className="text-white/80">Discover contractors, manufacturers, traders, distributors, and service providers across India.</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 mb-8">
          <div className="flex items-center mb-4">
            <Filter className="h-5 w-5 mr-2 text-navy" />
            <h3 className="text-lg font-semibold">Search & Filter</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <input type="text" placeholder="Search by name" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="px-4 py-2 rounded-md border border-input bg-background" data-testid="vendor-search-input" />
            <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="px-4 py-2 rounded-md border border-input bg-background" data-testid="vendor-category-select">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <select value={filters.business_type} onChange={(e) => setFilters({ ...filters, business_type: e.target.value })} className="px-4 py-2 rounded-md border border-input bg-background" data-testid="vendor-businesstype-select">
              <option value="">All Business Types</option>
              <option value="Manufacturer">Manufacturer</option>
              <option value="Trader">Trader</option>
              <option value="Contractor">Contractor</option>
              <option value="Service Provider">Service Provider</option>
              <option value="Distributor">Distributor</option>
            </select>
            <input type="text" placeholder="State" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} className="px-4 py-2 rounded-md border border-input bg-background" data-testid="vendor-state-input" />
            <input type="text" placeholder="District" value={filters.district} onChange={(e) => setFilters({ ...filters, district: e.target.value })} className="px-4 py-2 rounded-md border border-input bg-background" data-testid="vendor-district-input" />
          </div>
          <button onClick={fetchSuppliers} className="mt-4 px-6 py-2 rounded-md bg-navy text-white font-semibold" data-testid="vendor-search-btn">
            <Search className="inline h-4 w-4 mr-2" /> Search Vendors
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div></div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Building2 className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No suppliers found matching your criteria.</p>
            <p className="text-sm mt-2">Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {suppliers.map(s => (
              <div key={s.id} className="p-6 rounded-lg border border-border bg-card hover:shadow-lg transition-shadow" data-testid={`vendor-card-${s.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-navy">{s.company_name || 'Unnamed'}</h3>
                  {s.business_type && <span className="px-2 py-1 rounded text-xs font-medium bg-gold/20 text-navy">{s.business_type}</span>}
                </div>
                <div className="space-y-2 text-sm text-muted-foreground mb-4">
                  {s.contact_person && <div><span className="font-medium text-foreground">Contact:</span> {s.contact_person}</div>}
                  {s.category && <div className="flex items-center"><Briefcase className="h-4 w-4 mr-2" />{s.category}</div>}
                  {(s.state || s.service_state) && <div className="flex items-center"><MapPin className="h-4 w-4 mr-2" />{s.city ? `${s.city}, ` : ''}{s.district ? `${s.district}, ` : ''}{s.state || s.service_state}</div>}
                  {s.experience && <div><span className="font-medium text-foreground">Experience:</span> {s.experience}</div>}
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => setSelectedSupplier(s)} className="flex-1 px-3 py-2 rounded-md bg-navy text-white text-sm font-semibold hover:opacity-90" data-testid={`vendor-inquire-${s.id}`}>
                    Send Inquiry
                  </button>
                  <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi, I'm interested in services from ${s.company_name}`)}`} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-md bg-orange text-white text-sm font-semibold hover:opacity-90" data-testid={`vendor-whatsapp-${s.id}`}>
                    <MessageCircle className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedSupplier && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedSupplier(null)}>
            <div className="bg-card rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-semibold text-navy mb-4">Send Inquiry to {selectedSupplier.company_name}</h3>
              <form onSubmit={handleSubmitInquiry} className="space-y-3">
                <input type="text" placeholder="Your Name" value={inquiryForm.name} onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })} required className="w-full px-4 py-2 rounded-md border border-input bg-background" data-testid="inquiry-name-input" />
                <input type="email" placeholder="Your Email" value={inquiryForm.email} onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })} required className="w-full px-4 py-2 rounded-md border border-input bg-background" data-testid="inquiry-email-input" />
                <input type="tel" placeholder="Your Mobile" value={inquiryForm.mobile} onChange={(e) => setInquiryForm({ ...inquiryForm, mobile: e.target.value })} className="w-full px-4 py-2 rounded-md border border-input bg-background" data-testid="inquiry-mobile-input" />
                <textarea placeholder="Your Message / Requirement" value={inquiryForm.message} onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })} required rows={4} className="w-full px-4 py-2 rounded-md border border-input bg-background" data-testid="inquiry-message-input" />
                <div className="flex space-x-2">
                  <button type="button" onClick={() => setSelectedSupplier(null)} className="flex-1 px-4 py-2 rounded-md border border-input bg-background">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 rounded-md bg-navy text-white font-semibold" data-testid="inquiry-submit-btn">Send Inquiry</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDiscovery;
